import React from 'react';
import { Button, Burger } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import {
  HeaderWrapper,
  HeaderContent,
  InnerContainer,
  LeftGroup,
  LogoInternalLink,
  Logo,
  DesktopNav,
  InternalNavLink,
  ButtonGroup,
  MobileBurger,
  MobileNav,
  MobileInternalNavLink,
  MobileButtonGroup
} from './MarketingHeader.styles';
import logoSrc from '../../../../assets/images/logo.svg';

/**
 * MarketingHeader Component
 *
 * A marketing-style header for the registration page that matches
 * the marketing site's header design. Provides navigation links
 * to marketing pages and a login button.
 *
 * Features:
 * - Logo linking to home page
 * - Desktop navigation with internal page links
 * - Login button linking to /login
 * - Mobile responsive with burger menu
 * - Sticky positioning at top of viewport
 */
export const MarketingHeader: React.FC = (): React.ReactElement => {
  const [opened, { toggle, close }] = useDisclosure(false);

  const handleNavClick = (): void => {
    close();
  };

  return (
    <HeaderWrapper>
      <HeaderContent>
        <InnerContainer>
          <LeftGroup>
            <LogoInternalLink to="/" aria-label="MentionedMe - Go to home page">
              <Logo src={logoSrc} alt="MentionedMe" />
            </LogoInternalLink>

            <DesktopNav>
              <InternalNavLink to="/benefits">Benefits</InternalNavLink>
              <InternalNavLink to="/features">Features</InternalNavLink>
              <InternalNavLink to="/examples">Examples</InternalNavLink>
              <InternalNavLink to="/how-it-works">How It Works</InternalNavLink>
              <InternalNavLink to="/social-impact">For Non-Profits</InternalNavLink>
              <InternalNavLink to="/contact">Contact</InternalNavLink>
            </DesktopNav>
          </LeftGroup>

          <ButtonGroup>
            <Button
              component={Link}
              to="/login"
              variant="subtle"
              color="violet"
            >
              Login
            </Button>
          </ButtonGroup>

          <MobileBurger>
            <Burger opened={opened} onClick={toggle} aria-label="Toggle navigation" />
          </MobileBurger>
        </InnerContainer>

        {opened && (
          <MobileNav>
            <MobileInternalNavLink to="/benefits" onClick={handleNavClick}>
              Benefits
            </MobileInternalNavLink>
            <MobileInternalNavLink to="/features" onClick={handleNavClick}>
              Features
            </MobileInternalNavLink>
            <MobileInternalNavLink to="/examples" onClick={handleNavClick}>
              Examples
            </MobileInternalNavLink>
            <MobileInternalNavLink to="/how-it-works" onClick={handleNavClick}>
              How It Works
            </MobileInternalNavLink>
            <MobileInternalNavLink to="/social-impact" onClick={handleNavClick}>
              For Non-Profits
            </MobileInternalNavLink>
            <MobileInternalNavLink to="/contact" onClick={handleNavClick}>
              Contact
            </MobileInternalNavLink>
            <MobileButtonGroup>
              <Button
                component={Link}
                to="/login"
                variant="subtle"
                color="violet"
                fullWidth
                onClick={handleNavClick}
              >
                Login
              </Button>
            </MobileButtonGroup>
          </MobileNav>
        )}
      </HeaderContent>
    </HeaderWrapper>
  );
};

export default MarketingHeader;
